import React, { useContext, useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, AuthContext } from 'AuthContext';
import PrivateRoute from 'PrivateRoute';

import Dashboard from 'components/Dashboard';
import ChangePasswordPage from 'components/ChangePasswordPage';
import FicheLocataire from 'components/FicheLocataire';
import FicheBienAuto from 'components/FicheBienAuto'
import FicheLocataireAuto from './components/FicheLocataireAuto';
import FicheLotAuto from './components/FicheLotAuto';
import FicheSCIAuto from './components/FicheSCIAuto';
import Login from 'components/Login';
import Logout from 'components/Logout';
import UserBanner from 'components/UserBanner';
import ResetPasswordForm from 'components/ResetPasswordForm';
import SearchLocataires from 'components/SearchLocataires';
import SidebarMenu from 'components/SideBarMenu';


import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Toolbar from '@mui/material/Toolbar';

function AppInner() {
  const { token, role, locataire_id, loading, mustChangePassword } = useContext(AuthContext);

  if (loading) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route path="/reset-password" element={<ResetPasswordForm />} />
      <Route path="/logout" element={<Logout />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />

      {/* Redirect any other route to RequireAuth */}
      <Route
        path="/*"
        element={<RequireAuth role={role} token={token} locataire_id={locataire_id} mustChangePassword={mustChangePassword} />}
      />
    </Routes>
  );
}

function RequireAuth({ role, token, locataire_id, mustChangePassword }) {
  const [selectedSCI, setSelectedSCI] = useState("");
  const { logout, setGlobalError } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (token && role !== "admin" && role !== "locataire") {
      setGlobalError("Unknown role or forbidden access");
      logout();
      navigate("/", { replace: true });
    }
  }, [token, role, logout, navigate, setGlobalError]);

  if (!token) {
    return <Login />;
  }

  if (role === "locataire" && mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  if (role === "admin") {
    return (
      <>
        <UserBanner />
        <Toolbar />
        <Box sx={{ display: 'flex' }}>
          <SidebarMenu sciId={selectedSCI} />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 3,
              ml: '160px',
              width: '100%',
            }}
          >
            <Routes>
              <Route
                path="/"
                element={
                  <PrivateRoute roleRequired="admin">
                    <Dashboard
                      selectedSCI={selectedSCI}
                      setSelectedSCI={setSelectedSCI}
                    />
                  </PrivateRoute>
                }
              />
              <Route
                path="/locataires"
                element={
                  <PrivateRoute roleRequired="admin">
                    <SearchLocataires />
                  </PrivateRoute>
                }
              />
              <Route
                path="/locataires/:id"
                element={
                  <PrivateRoute roleRequired="admin">
                    <FicheLocataireAuto />
                  </PrivateRoute>
                }
              />
              <Route
                path="/lots/:id"
                element={
                  <PrivateRoute roleRequired="admin">
                    <FicheLotAuto />
                  </PrivateRoute>
                }
              />
              <Route
                path="/biens/:id"
                element={
                  <PrivateRoute roleRequired="admin">
                    <FicheBienAuto />
                  </PrivateRoute>
                }
              />
              <Route
                path="/scis/:id"
                element={
                  <PrivateRoute roleRequired="admin">
                    <FicheSCIAuto setSelectedSCI={setSelectedSCI} />
                  </PrivateRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Box>
        </Box>
      </>
    );
  }

  if (role === "locataire") {
    return (
      <>
        <UserBanner />
        <Toolbar />
        <Box>
          <Routes>
            <Route
              path="/"
              element={
                <PrivateRoute roleRequired="locataire">
                  <FicheLocataire locataireId={locataire_id} />
                </PrivateRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Box>
      </>
    );
  }

  return null;
}

function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

export default App;
